import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'

import { SearchOutlined, PlusOutlined, FileTextOutlined, QuestionCircleFilled, ThunderboltFilled } from '@ant-design/icons'
import { DEFAULT_SELECT_PAGE_SIZE } from '@config/apiConfig'
import { useMerchant } from '@contexts/operator/MerchantContext'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useFeatureEnabled } from '@contexts/operator/PlanContext'
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext'
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService'
import PageHeader from '@shared/PageHeader'
import { Card, Form, Row, Col, Input, Select, DatePicker, Checkbox, Button, Space, message, Table, Collapse, Segmented, Spin, Drawer, Tag, Alert, Empty, Radio, InputNumber, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import MerchantSelect from '@components/operator/MerchantSelect'
import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent'
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar'
import { fulfillmentService } from '@services/operator/fulfillmentService'
import { pickingListService } from '@services/operator/pickingListService'
import { pickingListPresetService } from '@services/operator/pickingListPresetService'
import PickingConceptModal from '@shared/picking_lists/PickingConceptModal'
import SearchFormConfigModal, { DEFAULT_CONFIG } from './components/SearchFormConfigModal'
import { formatDateTime } from '@utils/dateTime'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

const { Option } = Select

const PICKING_METHOD_STORAGE_KEY = 'picking_list_generate_picking_method'

export default function OperatorCreatePickingList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { addJob } = useOperatorNotification()
  const appPickingEnabled = useFeatureEnabled('picking_lists.app_picking')
  // 揀貨預設為 Pro 以上功能（PlanRegistry: standard=false）。標準版不可呼叫 presets 端點，
  // 否則後端 require_feature 'picking_list_presets' 會回 403 並彈出「此功能未開放於當前方案」。
  const presetsEnabled = useFeatureEnabled('picking_list_presets')
  const { merchantId } = useMerchant()
  // 使用 hook 版本的 productService，會自動從 MerchantContext 注入 merchant_id
  const productService = useOperatorProductService()

  const {
    merchantShippingTypes,
    merchantSalesChannels,
    merchant,
  } = useOperatorData()

  // 概念說明 Modal
  const [conceptModalVisible, setConceptModalVisible] = useState(false)

  // 設定
  const [fieldConfig, setFieldConfig] = useState(DEFAULT_CONFIG)

  // Drawer Visibility
  const [drawerVisible, setDrawerVisible] = useState(false)

  // 篩選結果
  const [fulfillments, setFulfillments] = useState([])
  const [hasSearched, setHasSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingTip, setLoadingTip] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const listRef = useRef(null)

  useEffect(() => {
    if (hasSearched && !loading && fulfillments.length > 0) {
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [hasSearched, loading, fulfillments.length])

  // 套用篩選範本
  const [presets, setPresets] = useState([])
  const [presetsLoading, setPresetsLoading] = useState(false)
  const [selectedPresetId, setSelectedPresetId] = useState(null)

  // 動態載入的資料
  const [products, setProducts] = useState([])
  const [productsLoading, setProductsLoading] = useState(false)
  // 已選商品 id → label 的 fallback 快取，避免新搜尋結果中找不到原已選項時 label 消失
  const [selectedProductMap, setSelectedProductMap] = useState(() => new Map())
  // 防止快速連續請求時舊回應覆蓋新回應
  const productsRequestIdRef = useRef(0)


  // 載入 Products（支援 server-side search）
  // productService hook 會自動從 MerchantContext 注入 merchant_id
  const loadProducts = async (keyword) => {
    if (!merchantId) {
      return
    }

    const myId = ++productsRequestIdRef.current
    setProductsLoading(true)
    try {
      const params = { page_size: 50 }
      if (keyword) {
        params.search = keyword
      }
      const data = await productService.fetchList(params)

      // 已被新請求取代，丟棄舊結果
      if (myId !== productsRequestIdRef.current) return

      if (data.success) {
        setProducts(data.data || [])
      }
    } catch (error) {
      if (myId === productsRequestIdRef.current) {
        console.error('Failed to load products:', error)
        message.error(t('common.error.load_failed'))
      }
    } finally {
      if (myId === productsRequestIdRef.current) {
        setProductsLoading(false)
      }
    }
  }

  // onFocus：只在 products 仍為空時預載前 50 筆，避免覆蓋當前搜尋結果
  const handleProductsFocus = () => {
    if (products.length === 0) {
      loadProducts()
    }
  }

  // 搜尋：300ms debounce，trim 後若空字串則回到瀏覽列表
  const handleProductSearch = useMemo(
    () =>
      debounce((value) => {
        const keyword = (value || '').trim()
        if (!keyword) {
          loadProducts()
        } else {
          loadProducts(keyword)
        }
      }, 300),
    [merchantId]
  )

  // onChange：把當前選中項的 label 寫入 selectedProductMap，作為 fallback 顯示來源
  const handleContainsProductsChange = (_value, selectedOptions) => {
    setSelectedProductMap((prev) => {
      const next = new Map(prev)
      const opts = Array.isArray(selectedOptions) ? selectedOptions : (selectedOptions ? [selectedOptions] : [])
      opts.forEach((opt) => {
        if (opt && opt.value != null && opt.label != null) {
          next.set(opt.value, opt.label)
        }
      })
      return next
    })
  }

  // merchantId 切換時清空已選 label 快取與 products，避免跨 merchant 殘留
  useEffect(() => {
    setSelectedProductMap(new Map())
    setProducts([])
  }, [merchantId])

  // 卸載 / merchantId 切換時取消 pending 的 debounce search
  useEffect(() => {
    return () => {
      handleProductSearch.cancel()
    }
  }, [handleProductSearch])

  // 初始化表單預設值
  useEffect(() => {
    const savedPickingMethod = localStorage.getItem(PICKING_METHOD_STORAGE_KEY)
    const pickingMethod = savedPickingMethod === 'paper' || (savedPickingMethod === 'app' && appPickingEnabled)
      ? savedPickingMethod
      : (appPickingEnabled ? 'app' : 'paper')
    form.setFieldsValue({
      taskType: 'OrderItem', // Default to Order Picking
      shippingDateScope: 'today',
      shippingDateRange: [dayjs().startOf('day'), dayjs().endOf('day')],
      sliceSize: 50,
      closeDate: dayjs(),
      deliveryOption: 'today',
      pickingMethod,
      sameProductFilter: 'none',
      noteFilter: 'none'
    })
  }, [form, appPickingEnabled])

  // 載入篩選範本（包含該 merchant 的及通用範本）
  const loadPresets = async () => {
    setPresetsLoading(true)
    try {
      const params = { page_size: 100 }
      const data = await pickingListPresetService.getRecords(params)
      if (data.success) {
        // 篩選：該 merchant 的範本 + 通用範本（無指定 merchant）
        const filtered = (data.data || []).filter(p =>
          !p.merchant || p.merchant.id === merchantId
        )
        setPresets(filtered)
      }
    } catch (error) {
      console.error('Failed to load presets:', error)
    } finally {
      setPresetsLoading(false)
    }
  }

  // 當 merchantId 改變時，載入篩選範本（僅 Pro 以上；標準版不打 presets 端點避免 403）
  useEffect(() => {
    if (merchantId && presetsEnabled) {
      loadPresets()
    }
  }, [merchantId, presetsEnabled])

  // 搜尋出貨單（使用篩選範本或手動篩選）
  const handleSearch = async () => {
    // merchantId 必填檢查
    if (!merchantId) {
      message.error(t('shared.picking_lists.validation.merchant_required'))
      return
    }

    try {
      setLoading(true)

      // 如果選擇了篩選範本，使用 simulate 端點
      if (selectedPresetId) {
        setLoadingTip(t('shared.picking_lists.generate.messages.search_loading'))
        const data = await pickingListPresetService.simulate(selectedPresetId)

        if (data.success) {
          const allFulfillments = data.data || []
          setFulfillments(allFulfillments)
          setSelectedRowKeys(allFulfillments.map(f => f.id))
          setHasSearched(true)
          message.success(t('shared.picking_lists.generate.messages.search_success_count', { count: allFulfillments.length }))
        } else {
          throw new Error(data.message || 'Simulate failed')
        }
      } else {
        // 手動篩選模式
        const MAX_LOOPS = 20
        let allFulfillments = []
        let nextSinceId = null
        let hasMore = true
        let loopCount = 0

        setLoadingTip(t('common.loading', { count: 0 }))

        const values = await form.validateFields()
        const filterParams = buildFilterParams(values)
        const limit = values.fulfillmentsLimit ? parseInt(values.fulfillmentsLimit) : Infinity
        const pageSize = Math.min(100, limit)

        while (hasMore && loopCount < MAX_LOOPS && allFulfillments.length < limit) {
          loopCount++
          setLoadingTip(t('shared.picking_lists.generate.messages.search_loading_page', { page: loopCount, count: allFulfillments.length }))

          const data = await fulfillmentService.fetchList({
            ...filterParams,
            page_size: pageSize,
            since_id: nextSinceId,
            direction: 'desc'
          })

          if (data.success) {
            const newFulfillments = data.data || []
            allFulfillments = [...allFulfillments, ...newFulfillments]

            hasMore = data.has_more
            nextSinceId = data.since_id

            if (newFulfillments.length === 0) {
              hasMore = false
            }
          } else {
            throw new Error(data.message || 'Search failed')
          }
        }

        if (allFulfillments.length > limit) {
          allFulfillments = allFulfillments.slice(0, limit)
        }

        setFulfillments(allFulfillments)
        setSelectedRowKeys(allFulfillments.map(f => f.id))
        setHasSearched(true)

        if (loopCount >= MAX_LOOPS && hasMore) {
          message.warning(t('shared.picking_lists.generate.messages.search_limit_reached', { max: MAX_LOOPS * 100 }))
        } else {
          message.success(t('shared.picking_lists.generate.messages.search_success_count', { count: allFulfillments.length }))
        }
      }

    } catch (error) {
      console.error('Search error:', error)
      message.error(t('common.error.search_failed'))
    } finally {
      setLoading(false)
      setLoadingTip('')
    }
  }

  // 構建篩選參數
  const buildFilterParams = (values) => {
    const params = {}

    // Merchant ID - 直接使用 context 中的 merchantId
    if (merchantId) {
      params['fulfillments.merchant_id'] = [merchantId]
    }

    // Scheduled Shipping Date filter
    // 方案 A: 「今天」= 出貨日是今天 OR 出貨日為空
    const { shippingDateScope, shippingDateRange } = values
    if (shippingDateScope === 'today') {
      params['orders.scheduled_shipping_date_or_null'] = {
        start: dayjs().startOf('day'),
        end: dayjs().endOf('day')
      }
    } else if (shippingDateScope === 'custom' && shippingDateRange && shippingDateRange.length === 2) {
      params['orders.scheduled_shipping_date_or_null'] = {
        start: shippingDateRange[0],
        end: shippingDateRange[1]
      }
    }
    // shippingDateScope === 'all' 不加篩選

    // Merchant Channel
    if (values.salesChannelIds && values.salesChannelIds.length > 0) {
      params['orders.sales_channel_id'] = values.salesChannelIds
    }

    // Shipping Types — 走 fulfillment 層（authoritative，與詳細頁 change_shipping_type 同源）
    if (values.shippingTypeIds && values.shippingTypeIds.length > 0) {
      params['fulfillments.shipping_type_id'] = values.shippingTypeIds
    }

    // Contains Products
    if (values.containsProducts && values.containsProducts.length > 0) {
      params['orders.product_ids'] = values.containsProducts
    }

    // Order Names
    if (values.orderNames) {
      params['orders.order_names'] = values.orderNames
    }

    // Status filter - only allocated fulfillments (Ready to Ship)
    params['fulfillments.status'] = ['allocated']

    // Only show fulfillments without a picking wave
    params['fulfillments.picking_wave_id'] = ['null']

    // Source Type (Task Type)
    const { taskType } = values
    if (taskType && taskType !== 'all') {
      params['fulfillments.source_type'] = taskType
    }

    // Advanced Filters
    if (values.productsCount?.value != null) params['orders.products_count'] = values.productsCount
    if (values.totalquantityCount?.value != null) params['orders.total_quantity_count'] = values.totalquantityCount
    if (values.sameProductFilter === 'sameItems') {
      params['orders.same_items'] = true
    } else if (values.sameProductFilter === 'sameItemsAndQuantity') {
      params['orders.same_items'] = true
      params['orders.same_quantity'] = true
    }
    if (values.noteFilter === 'noNote') {
      params['orders.note_filter'] = 'no_note'
    } else if (values.noteFilter === 'hasNote') {
      params['orders.note_filter'] = 'has_note'
    }
    if (values.urgent) params['orders.urgent'] = true

    return params
  }

  const handleOpenDrawer = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.validation.required'))
      return
    }
    setDrawerVisible(true)
  }

  // 本地搜尋
  const [localSearchKeyword, setLocalSearchKeyword] = useState('')

  // 使用 useMemo 優化 filteredFulfillments，避免每次渲染都重新計算
  // 排序：urgent 訂單優先排在最上面
  const filteredFulfillments = useMemo(() => {
    let result = fulfillments
    if (localSearchKeyword) {
      const keyword = localSearchKeyword.toLowerCase()
      result = result.filter(item => {
        if (item.name && item.name.toLowerCase().includes(keyword)) return true
        const orders = item.orders || []
        return orders.some(o =>
          (o.name && o.name.toLowerCase().includes(keyword)) ||
          (o.receiver_name && o.receiver_name.toLowerCase().includes(keyword))
        )
      })
    }
    // urgent 排在前面，同群組內維持原排序
    return [...result].sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0))
  }, [fulfillments, localSearchKeyword])

  // 產生揀貨單
  const handleConfirmCreate = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // 構建 payload
      const groupBy = []
      if (values.separateChannel) groupBy.push('channel')
      if (values.separateShipments) groupBy.push('shipping_type')

      const payload = {
        merchant_id: merchantId,
        fulfillment_ids: selectedRowKeys,
        picking_method: values.pickingMethod,
        picking_list_preset_id: selectedPresetId || undefined,
        grouping: {
          by: groupBy,
          max_orders: values.sliceSize,
          max_quantity: values.maxQuantity || undefined,
          max_product_types: values.maxItems || undefined,
        }
      }

      const data = await pickingListService.create(payload)

      if (data.success && data.data?.job_id) {
        message.success(t('shared.picking_lists.generate.messages.create_success_background'))
        addJob(data.data.job_id, 'shared.picking_lists.generate.job_name')
        navigate('/picking_lists')
      } else {
        message.error(t('common.error.create_failed'))
      }

    } catch (error) {
      console.error('Create error:', error)
      message.error(t('common.error.create_failed'))
    } finally {
      setLoading(false)
    }
  }

  // Table columns - 使用 useMemo 避免每次渲染都重新創建
  const columns = useMemo(() => [
    {
      title: t('models.fulfillment.number'),
      dataIndex: 'number',
      key: 'number',
      width: 150,
      render: (number, record) => (
        <Space>
          {record.urgent && <ThunderboltFilled style={{ color: '#ff4d4f' }} />}
          <span>{number}</span>
        </Space>
      ),
    },
    // 一張 fulfillment 可能合併多筆訂單（合單），所以這四欄都從 record.orders[] 取，
    // 並去重後以逗號串接顯示。Backend 將這些欄位 nest 在 orders[] 之下而非 fulfillment top-level。
    {
      title: t('models.order.name'),
      key: 'order_names',
      width: 150,
      render: (_, record) => (record.orders || []).map(o => o.name).filter(Boolean).join(', '),
    },
    {
      title: t('models.order.receiver_name'),
      key: 'receiver_name',
      width: 120,
      render: (_, record) => {
        const names = (record.orders || []).map(o => o.receiver_name).filter(Boolean)
        return [...new Set(names)].join(', ')
      },
    },
    {
      title: t('models.order.channel'),
      key: 'sales_channel_name',
      width: 100,
      render: (_, record) => {
        const channels = (record.orders || []).map(o => o.sales_channel_name).filter(Boolean)
        return [...new Set(channels)].join(', ')
      },
    },
    {
      title: t('models.order.shipping_type'),
      key: 'shipping_type',
      width: 100,
      // fulfillment 一張僅一個 shipping_type（authoritative）；不再 per-order map
      render: (_, record) => record.shipping_type?.name || '-',
    },
    {
      title: t('models.order.total_quantity'),
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 80,
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => formatDateTime(text),
    },
  ], [t])

  // 使用 useCallback 優化 onChange，避免每次渲染都創建新函數
  const handleRowSelectionChange = useCallback((newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }, [])

  // 使用 useMemo 優化 rowSelection 物件
  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: handleRowSelectionChange,
  }), [selectedRowKeys, handleRowSelectionChange])

  const pickingMethodWatch = Form.useWatch('pickingMethod', form);

  useEffect(() => {
    if (pickingMethodWatch === 'app' || pickingMethodWatch === 'paper') {
      localStorage.setItem(PICKING_METHOD_STORAGE_KEY, pickingMethodWatch)
    }
  }, [pickingMethodWatch])

  const fieldRenderers = {
    // 1. Basic Info & Time (Source, Destination, Time)
    taskType: () => (
      <Col span={8} key="taskType">
        <Form.Item
          name="taskType"
          label={t('shared.picking_lists.generate.filters.task_type')}
        >
          <Select>
            <Option value="all">{t('common.all')}</Option>
            <Option value="OrderItem">{t('enums.fulfillment.source_type.OrderItem')}</Option>
            <Option value="WorkOrderItem">{t('enums.fulfillment.source_type.WorkOrderItem')}</Option>
            <Option value="StockTransferOrder">{t('enums.fulfillment.source_type.StockTransferOrder')}</Option>
          </Select>
        </Form.Item>
      </Col>
    ),
    salesChannelIds: () => (
      <Col span={8} key="salesChannelIds">
        <Form.Item
          name="salesChannelIds"
          label={t('models.sales_channel.name')}
        >
          <Select
            mode="multiple"
            placeholder={t('common.placeholder.please_select')}
            allowClear
          >
            {merchantSalesChannels && merchantSalesChannels.map(channel => (
              <Option key={channel.id} value={channel.id}>
                {channel.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    ),
    shippingTypeIds: () => (
      <Col span={8} key="shippingTypeIds">
        <Form.Item
          name="shippingTypeIds"
          label={t('models.order.shipping_type')}
        >
          <Select
            mode="multiple"
            placeholder={t('common.placeholder.please_select')}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {merchantShippingTypes.map(type => (
              <Option key={type.id} value={type.id}>
                {t(`shipping_types.${type.code}`, { defaultValue: type.name })}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    ),
    dateRange: () => (
      <Col span={16} key="dateRange">
        <Form.Item
          label={t('shared.picking_lists.generate.filters.shipping_date_scope')}
          style={{ marginBottom: 0 }}
          shouldUpdate={(prevValues, currentValues) => prevValues.shippingDateScope !== currentValues.shippingDateScope}
        >
          {({ getFieldValue }) => {
            const shippingDateScope = getFieldValue('shippingDateScope') || 'today';
            const isCustomScope = shippingDateScope === 'custom';

            return (
              <Row gutter={8}>
                <Col>
                  <Form.Item name="shippingDateScope" noStyle>
                    <Segmented
                      options={[
                        { label: t('shared.picking_lists.generate.filters.scope_today'), value: 'today' },
                        { label: t('shared.picking_lists.generate.filters.scope_all'), value: 'all' },
                        { label: t('shared.picking_lists.generate.filters.scope_custom'), value: 'custom' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                {isCustomScope && (
                  <Col flex="auto">
                    <Form.Item name="shippingDateRange" noStyle>
                      <FullDayRangePicker />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            );
          }}
        </Form.Item>
      </Col>
    ),
    // 4. Flags (Booleans/Characteristics)
    containsProducts: () => {
      // 顯示在下拉中的 ids：products + selectedProductMap 中還沒在 products 裡的 fallback
      const productIds = new Set(products.map((p) => p.id))
      const fallbackEntries = []
      selectedProductMap.forEach((label, id) => {
        if (!productIds.has(id)) {
          fallbackEntries.push({ id, label })
        }
      })
      return (
        <Col span={8} key="containsProducts">
          <Form.Item
            name="containsProducts"
            label={t('shared.picking_lists.generate.filters.contains_products')}
          >
            <Select
              mode="multiple"
              placeholder={t('shared.picking_lists.generate.filters.contains_products_placeholder')}
              allowClear
              showSearch
              loading={productsLoading}
              onFocus={handleProductsFocus}
              onSearch={handleProductSearch}
              onChange={handleContainsProductsChange}
              filterOption={false}
              optionLabelProp="label"
            >
              {products.map(product => {
                const label = `${product.name} (${product.sku})`;
                return (
                  <Option key={product.id} value={product.id} label={label}>
                    {label}
                  </Option>
                );
              })}
              {fallbackEntries.map(({ id, label }) => (
                <Option key={id} value={id} label={label}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )
    },
    orderNames: () => (
      <Col span={8} key="orderNames">
        <Form.Item
          name="orderNames"
          label={t('shared.picking_lists.generate.filters.order_names')}
        >
          <Input placeholder={t('common.placeholder.please_enter')} />
        </Form.Item>
      </Col>
    ),
    productsCount: () => (
      <Col span={8} key="productsCount">
        <Form.Item label={t('models.order.products_count')} style={{ marginBottom: 0 }}>
          <Space.Compact block>
            <Form.Item name={['productsCount', 'op']} noStyle initialValue="eq">
              <Select style={{ width: '80px' }}>
                <Option value="eq">=</Option>
                <Option value="gte">&ge;</Option>
                <Option value="lte">&le;</Option>
              </Select>
            </Form.Item>
            <Form.Item name={['productsCount', 'value']} noStyle>
              <InputNumber min={0} style={{ width: 'calc(100% - 80px)' }} placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
      </Col>
    ),
    totalquantityCount: () => (
      <Col span={8} key="totalquantityCount">
        <Form.Item label={t('models.order.total_quantity_count')} style={{ marginBottom: 0 }}>
          <Space.Compact block>
            <Form.Item name={['totalquantityCount', 'op']} noStyle initialValue="eq">
              <Select style={{ width: '80px' }}>
                <Option value="eq">=</Option>
                <Option value="gte">&ge;</Option>
                <Option value="lte">&le;</Option>
              </Select>
            </Form.Item>
            <Form.Item name={['totalquantityCount', 'value']} noStyle>
              <InputNumber min={0} style={{ width: 'calc(100% - 80px)' }} placeholder={t('common.placeholder.please_enter')} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
      </Col>
    ),
    sameProductFilter: () => (
      <Col span={16} key="sameProductFilter">
        <Form.Item
          name="sameProductFilter"
          label={t('shared.picking_lists.generate.filters.same_product_filter')}
        >
          <Segmented
            block
            options={[
              { label: t('shared.picking_lists.generate.filters.same_product_filter_none'), value: 'none' },
              { label: t('shared.picking_lists.generate.filters.same_product_filter_same_items'), value: 'sameItems' },
              { label: t('shared.picking_lists.generate.filters.same_product_filter_same_items_and_quantity'), value: 'sameItemsAndQuantity' },
            ]}
          />
        </Form.Item>
      </Col>
    ),
    noteFilter: () => (
      <Col span={8} key="noteFilter">
        <Form.Item
          name="noteFilter"
          label={t('shared.picking_lists.generate.filters.note_filter')}
        >
          <Segmented
            block
            options={[
              { label: t('shared.picking_lists.generate.filters.note_filter_none'), value: 'none' },
              { label: t('shared.picking_lists.generate.filters.note_filter_no_note'), value: 'noNote' },
              { label: t('shared.picking_lists.generate.filters.note_filter_has_note'), value: 'hasNote' },
            ]}
          />
        </Form.Item>
      </Col>
    ),
    urgent: () => (
      <Col span={8} key="urgent">
        <Form.Item
          name="urgent"
          valuePropName="checked"
          label={t('models.order.urgent')}
        >
          <Checkbox>{t('shared.picking_lists.generate.filters.urgent_only')}</Checkbox>
        </Form.Item>
      </Col>
    ),
    fulfillmentsLimit: () => (
      <Col span={8} key="fulfillmentsLimit">
        <Form.Item
          name="fulfillmentsLimit"
          label={t('shared.picking_lists.generate.filters.fulfillments_limit')}
        >
          <InputNumber min={1} max={1000} style={{ width: '100%' }} placeholder={t('shared.picking_lists.generate.filters.orders_limit_placeholder')} />
        </Form.Item>
      </Col>
    ),
  }

  const renderFields = (mode) => {
    const keys = Object.keys(fieldRenderers).filter(key => fieldConfig[key] === mode)
    if (keys.length === 0) return null

    return (
      <Row gutter={16} wrap>
        {keys.map(key => fieldRenderers[key]())}
      </Row>
    )
  }

  const hasAdvancedFields = Object.values(fieldConfig).includes('advanced')

  return (
    <MerchantSelect>
      <div>
      <PageHeader
        items={[
          { label: t('operator.menu.picking.title') },
          { label: t('operator.menu.picking_lists.generate') },
        ]}
        guideButton={
          <Tooltip title={t('concepts.picking_lists.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
        <Spin spinning={loading} tip={loadingTip}>
        <Form
          form={form}
          layout="vertical"
        >
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <span>{t('shared.picking_lists.generate.title')}</span>
              </Space>
            }
            extra={<SearchFormConfigModal onConfigChange={setFieldConfig} />}
          >
            {/* 套用篩選範本 — Pro 以上才顯示（標準版無此功能，隱藏避免誤導與 403） */}
            {presetsEnabled && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Form.Item
                    label={t('shared.picking_lists.generate.filters.apply_preset')}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      allowClear
                      placeholder={t('shared.picking_lists.generate.filters.apply_preset_placeholder')}
                      loading={presetsLoading}
                      value={selectedPresetId}
                      onChange={(value) => setSelectedPresetId(value)}
                      notFoundContent={
                        <SelectNotFoundContent
                          hint={t('shared.picking_lists.generate.filters.no_presets')}
                          settingsPath="/operator/picking_lists/presets/new"
                          settingsLabel={t('shared.picking_lists.generate.filters.go_to_presets')}
                          onReload={loadPresets}
                          reloadLoading={presetsLoading}
                        />
                      }
                    >
                      {presets.map(preset => (
                        <Option key={preset.id} value={preset.id}>
                          {preset.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* 常用篩選（選擇範本時隱藏手動篩選） */}
            {!selectedPresetId && renderFields('primary')}

            {/* 進階篩選（選擇範本時隱藏） */}
            {!selectedPresetId && hasAdvancedFields && (
              <Collapse
                ghost
                style={{ marginBottom: 24 }}
                items={[
                  {
                    key: '1',
                    label: t('shared.picking_lists.generate.filters.advanced'),
                    children: renderFields('advanced'),
                  },
                ]}
              />
            )}

            <Row justify="end">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                {t('shared.picking_lists.generate.button.load_orders')}
              </Button>
            </Row>
          </Card>

          {/* Drawer for Options */}
          <Drawer
            title={t('shared.picking_lists.generate.options_title')}
            placement="right"
            width={500}
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            forceRender
            footer={
              <Row justify="end">
                <Space>
                  <Button onClick={() => setDrawerVisible(false)}>
                    {t('common.button.cancel')}
                  </Button>
                  <Button type="primary" onClick={handleConfirmCreate} loading={loading}>
                    {t('common.button.confirm')}
                  </Button>
                </Space>
              </Row>
            }
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="pickingMethod"
                  label={t('shared.picking_lists.generate.options.picking_method')}
                  rules={[{ required: true }]}
                  style={{ marginBottom: 8 }}
                >
                  <Segmented
                    block
                    options={[
                      { label: t('shared.picking_lists.generate.options.app'), value: 'app', disabled: !appPickingEnabled },
                      { label: t('shared.picking_lists.generate.options.paper'), value: 'paper' }
                    ]}
                  />
                </Form.Item>
                <Alert
                  message={
                    pickingMethodWatch === 'paper'
                      ? t('shared.picking_lists.generate.options.picking_method_help_paper')
                      : t('shared.picking_lists.generate.options.picking_method_help_app')
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              </Col>
            </Row>

            {/* 波次拆分規則 */}
            <Typography.Title level={5} style={{ marginBottom: 4 }}>
              {t('shared.picking_lists.generate.options.wave_split_title')}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('shared.picking_lists.generate.options.wave_split_description')}
            </Typography.Text>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="sliceSize"
                  label={
                    <Tooltip title={t('shared.picking_lists.generate.options.slice_size_help')}>
                      <span>{t('shared.picking_lists.generate.options.slice_size')}</span>
                    </Tooltip>
                  }
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="separateShipments"
                  valuePropName="checked"
                >
                  <Checkbox>{t('shared.picking_lists.generate.options.separate_by_shipping_types')}</Checkbox>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="separateChannel"
                  valuePropName="checked"
                >
                  <Checkbox>{t('shared.picking_lists.generate.options.separate_by_channel')}</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {/* 揀貨單拆分規則 */}
            <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
              {t('shared.picking_lists.generate.options.list_split_title')}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('shared.picking_lists.generate.options.list_split_description')}
            </Typography.Text>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="maxItems"
                  label={
                    <Tooltip title={t('shared.picking_lists.generate.options.max_items_help')}>
                      <span>{t('shared.picking_lists.generate.options.max_items')}</span>
                    </Tooltip>
                  }
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="maxQuantity"
                  label={
                    <Tooltip title={t('shared.picking_lists.generate.options.max_quantity_help')}>
                      <span>{t('shared.picking_lists.generate.options.max_quantity')}</span>
                    </Tooltip>
                  }
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
                </Form.Item>
              </Col>
            </Row>
          </Drawer>

        </Form>

        {/* 訂單列表 - 搜尋後才顯示 */}
        {hasSearched && (
          <div ref={listRef}>
          <Card
            title={t('shared.picking_lists.generate.orders_list')}
            style={{ marginTop: 16 }}
          >
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder={t('common.table.filters.search_placeholder')}
                allowClear
                onSearch={value => setLocalSearchKeyword(value)}
                onChange={e => setLocalSearchKeyword(e.target.value)}
                style={{ width: 300 }}
              />
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredFulfillments}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ y: 600 }}
              rowClassName={(record) => record.urgent ? 'picking-urgent-row' : ''}
            />
          </Card>
          </div>
        )}
      </Spin>

      {hasSearched && fulfillments.length > 0 && (
        <>
          <FloatingActionBarSpacer />
          <FloatingActionBar
            role="operator"
            onSubmit={handleOpenDrawer}
            submitText={t('shared.picking_lists.generate.button.create_picking_list')}
            submitIcon={<PlusOutlined />}
            disabled={selectedRowKeys.length === 0}
            extraButtons={
              <Space>
                <Tag color={selectedRowKeys.length > 0 ? 'blue' : 'default'} style={{ fontSize: '14px', padding: '4px 10px' }}>
                  {t('shared.picking_lists.generate.selection_summary', {
                    selected: selectedRowKeys.length,
                    total: fulfillments.length
                  })}
                </Tag>
                {selectedRowKeys.length > 0 && (
                  <Button onClick={() => setSelectedRowKeys([])}>
                    {t('common.button.deselect_all')}
                  </Button>
                )}
              </Space>
            }
          />
        </>
      )}

      <PickingConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
      </div>
    </MerchantSelect>
  )
}
